import React, { useEffect, useState, useContext, useRef } from 'react'
import axios from 'axios'
import AuthContext from '../../middlewares/AuthContext'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCommentDollar, faImage, faUpload } from '@fortawesome/free-solid-svg-icons'
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik'
import * as Yup from 'yup'
import Swal from 'sweetalert2'
import { useParams } from 'react-router-dom'
import utilities from '../../utilities/utilities'
import { useTranslation } from 'react-i18next'
import CustomLoader from '../layouts/CustomLoader'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'
const VALID_EXT  = ['png', 'jpg', 'jpeg', 'gif', 'bmp']
const MAX_BANNERS = 3

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface AdvertisingForm {
  phone   : string
  whatsapp: string
  email   : string
  links   : string[]
}

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.78rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
    {children}
  </label>
)

const Advertising: React.FC = () => {
  const { id }         = useParams<{ id: string }>()
  const { getHeaders } = useContext(AuthContext) as AuthCtx
  const { t }          = useTranslation()

  const fileRefs = useRef<(HTMLInputElement | null)[]>([])

  const [loaded, setLoaded]   = useState(false)
  const [loading, setLoading] = useState(false)
  const [images, setImages]   = useState<string[]>([])
  const [noSub, setNoSub]     = useState(false)
  const [initialValues, setInitialValues] = useState<AdvertisingForm>({
    phone: '', whatsapp: '', email: '', links: [],
  })

  useEffect(() => {
    const fetchFreemium = async () => {
      try {
        const headers = await getHeaders()
        const res     = await axios.get(`${url}marketings/freemium/${id}`, { headers })
        const payment = res.data?.message?.Markpayments?.[0]
        if (payment?.FreemiumId !== null && payment?.Freemium) {
          const freemium = payment.Freemium
          const _banners: string[] = freemium?.banners ?? []
          const imagePromises = _banners.map((ban: string) =>
            axios.get(`${url}freemium/${ban}`, { responseType: 'blob' })
              .then(r => new Promise<string>(resolve => {
                const reader = new FileReader()
                reader.onload = e => resolve(e.target?.result as string)
                reader.readAsDataURL(r.data)
              }))
              .catch(() => '')
          )
          const loaded = await Promise.all(imagePromises)
          setImages(loaded.filter(Boolean))
          const links = (freemium?.link ?? []).map((l: string) => l.replace('http://', ''))
          setInitialValues({
            phone   : freemium?.phone    ?? '',
            whatsapp: freemium?.whatsapp ?? '',
            email   : freemium?.email    ?? '',
            links,
          })
        }
      } catch {
        setNoSub(true)
      } finally {
        setLoaded(true)
      }
    }
    fetchFreemium()
  }, [])

  const handleImage = async (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const file = e.target.files?.[0]
    if (!file) return
    const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
    if (!VALID_EXT.includes(ext)) {
      if (fileRefs.current[index]) fileRefs.current[index]!.value = ''
      Swal.fire({
        icon: 'info', title: t('marketing.advertising.invalid_extension'),
        toast: true, position: 'top-end', showConfirmButton: false, timer: 3000, timerProgressBar: true,
      })
      return
    }
    const base64 = await utilities.getBase64(file) as string
    setImages(prev => {
      const next = [...prev]
      next[index] = base64
      return next
    })
    const input = document.getElementById(`file-label-${index}`) as HTMLInputElement | null
    if (input) input.value = file.name
  }

  const handleRemoveBanner = (index: number, remove: (i: number) => void) => {
    remove(index)
    setImages(prev => prev.filter((_, i) => i !== index))
    if (fileRefs.current[index]) fileRefs.current[index]!.value = ''
  }

  const uploadAdvertising = async (values: AdvertisingForm) => {
    const confirmed = await Swal.fire({
      title             : t('marketing.advertising.are_you_sure'),
      text              : t('marketing.advertising.upload_images'),
      icon              : 'info',
      showCancelButton  : true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor : '#d33',
      confirmButtonText : 'Yes, Upload!',
    })
    if (!confirmed.isConfirmed) return

    const linksArr = values.links.map(l => `http://${l}`)
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.post(`${url}marketings/freemium/${id}`, {
        ...values,
        images   : JSON.stringify(images),
        links_arr: JSON.stringify(linksArr),
      }, { headers })
      Swal.fire({ icon: 'success', title: res.data.response })
    } catch (err: any) {
      Swal.fire({
        icon : 'error',
        title: err?.response?.data?.response || 'Error',
        text : err?.response?.data?.message,
      })
    } finally {
      setLoading(false)
    }
  }

  const formSchema = Yup.object().shape({
    phone   : Yup.string().min(3).max(255).required(),
    whatsapp: Yup.string().max(255),
    email   : Yup.string().email().required(),
    links   : Yup.array()
      .of(Yup.string().required(t('utilities.is_a_required_field')).min(3))
      .required()
      .min(1),
  })

  if (!loaded) return <CustomLoader />

  if (noSub) return (
    <div style={{ padding: '2rem', textAlign: 'center', color: '#475569' }}>
      <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Subscription does not exist :(</h2>
    </div>
  )

  return (
    <div className="row justify-content-center">
      <div className="col-md-10 col-lg-8">

        {/* Header card */}
        <div style={{
          backgroundColor: '#fff',
          borderRadius   : 16,
          boxShadow      : '0 4px 20px rgba(0,0,0,0.07)',
          border         : '1px solid #f1f5f9',
          overflow       : 'hidden',
          marginBottom   : '1.5rem',
        }}>
          <div style={{ height: 4, background: `linear-gradient(90deg, ${BRAND_DARK}, ${BRAND})` }} />
          <div style={{ padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              backgroundColor: `${BRAND}18`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <FontAwesomeIcon icon={faCommentDollar} style={{ color: BRAND, fontSize: '1.1rem' }} />
            </div>
            <p style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
              {t('marketing.advertising.freemium')}
            </p>
          </div>
        </div>

        {/* Form card */}
        <div style={{
          backgroundColor: '#fff',
          borderRadius   : 16,
          boxShadow      : '0 4px 20px rgba(0,0,0,0.07)',
          border         : '1px solid #f1f5f9',
          overflow       : 'hidden',
        }}>
          <div style={{ height: 3, backgroundColor: BRAND }} />
          <div style={{ padding: '1.5rem' }}>

            <Formik
              initialValues={initialValues}
              validationSchema={formSchema}
              onSubmit={uploadAdvertising}
              enableReinitialize
            >
              {({ values }) => (
                <Form>

                  <div className="mb-4">
                    <FieldLabel>{t('marketing.advertising.phone')} *</FieldLabel>
                    <Field type="text" name="phone" className="form-control form-control-sm" />
                    <ErrorMessage name="phone" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                  </div>

                  <div className="mb-4">
                    <FieldLabel>WhatsApp</FieldLabel>
                    <Field type="text" name="whatsapp" className="form-control form-control-sm" />
                    <ErrorMessage name="whatsapp" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                  </div>

                  <div className="mb-4">
                    <FieldLabel>{t('marketing.advertising.email')} *</FieldLabel>
                    <Field type="text" name="email" className="form-control form-control-sm" />
                    <ErrorMessage name="email" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                  </div>

                  <hr style={{ borderColor: '#f1f5f9', margin: '1rem 0' }} />

                  <div className="mb-2">
                    <p style={{ fontWeight: 700, color: '#475569', marginBottom: '0.25rem', fontSize: '0.85rem' }}>
                      {t('marketing.advertising.you_can')}
                    </p>
                    <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: 0 }}>
                      {t('marketing.advertising.you_can_txt')}
                    </p>
                  </div>

                  <FieldArray name="links">
                    {({ remove, push }) => (
                      <div className="mb-4">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                          <FieldLabel>{t('marketing.advertising.banners_list')}</FieldLabel>
                          <button
                            type="button"
                            disabled={values.links.length >= MAX_BANNERS}
                            onClick={() => { if (values.links.length < MAX_BANNERS) push('') }}
                            style={{
                              display        : 'inline-flex',
                              alignItems     : 'center',
                              padding        : '0.3rem 0.9rem',
                              borderRadius   : 9999,
                              backgroundColor: values.links.length >= MAX_BANNERS ? '#94a3b8' : BRAND,
                              color          : '#fff',
                              fontWeight     : 700,
                              fontSize       : '0.82rem',
                              border         : 'none',
                              cursor         : values.links.length >= MAX_BANNERS ? 'not-allowed' : 'pointer',
                            }}
                          >
                            + Add banner
                          </button>
                        </div>

                        {values.links.map((_, index) => (
                          <div key={index} style={{
                            borderBottom  : '1px solid #f1f5f9',
                            marginBottom  : '1rem',
                            paddingBottom : '1rem',
                          }}>
                            <input
                              ref={el => { fileRefs.current[index] = el }}
                              type="file"
                              className="d-none"
                              id={`formFile${index}`}
                              accept=".png,.jpg,.jpeg,.gif,.bmp"
                              onChange={e => handleImage(e, index)}
                            />

                            {/* Image picker row */}
                            <div className="input-group mb-2">
                              <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={() => fileRefs.current[index]?.click()}
                              >
                                <FontAwesomeIcon icon={faImage} className="me-1" />
                                {t('marketing.advertising.select_image')}
                              </button>
                              <input
                                id={`file-label-${index}`}
                                type="text"
                                className="form-control form-control-sm bg-light"
                                readOnly
                                placeholder={images[index] ? 'Image loaded' : 'No file selected'}
                                onClick={() => fileRefs.current[index]?.click()}
                                style={{ cursor: 'pointer' }}
                              />
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleRemoveBanner(index, remove)}
                              >
                                &times;
                              </button>
                            </div>

                            {/* Link row */}
                            <div className="input-group mb-2">
                              <span className="input-group-text fw-bold" style={{ fontSize: '0.82rem' }}>http://</span>
                              <Field
                                type="text"
                                className="form-control form-control-sm"
                                name={`links.${index}`}
                                placeholder="mypage.com"
                              />
                            </div>
                            <ErrorMessage name={`links.${index}`} component="p" className="text-danger small fw-bold mb-0" />

                            {images[index] && (
                              <img
                                src={images[index]}
                                alt={`banner-${index}`}
                                style={{ marginTop: '0.5rem', width: 160, height: 90, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </FieldArray>

                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      display        : 'inline-flex',
                      alignItems     : 'center',
                      justifyContent : 'center',
                      gap            : '0.45rem',
                      width          : '100%',
                      padding        : '0.55rem 1.5rem',
                      borderRadius   : 9999,
                      backgroundColor: loading ? '#94a3b8' : BRAND,
                      color          : '#fff',
                      fontWeight     : 700,
                      fontSize       : '0.875rem',
                      border         : 'none',
                      cursor         : loading ? 'not-allowed' : 'pointer',
                      boxShadow      : loading ? 'none' : `0 4px 14px ${BRAND}55`,
                      transition     : 'filter 0.15s, transform 0.15s',
                    }}
                    onMouseEnter={e => { if (!loading) { e.currentTarget.style.filter = 'brightness(1.09)'; e.currentTarget.style.transform = 'translateY(-1px)' }}}
                    onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; e.currentTarget.style.transform = 'translateY(0)' }}
                  >
                    {loading
                      ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      : <FontAwesomeIcon icon={faUpload} />
                    }
                    <span>{t('marketing.advertising.upload_advertising')}</span>
                  </button>

                </Form>
              )}
            </Formik>
          </div>
        </div>

      </div>
    </div>
  )
}

export default Advertising
